import { useCallback, useMemo, useState } from "react";
import { CARDS, boardClock } from "@/data/goalsBoard";
import { PhoneFrame } from "@/shell/review/PhoneFrame";
import { DesktopFrame } from "@/shell/review/DesktopFrame";
import { DnaSheet } from "@/design/DnaSheet";
import { GoalsBoard } from "../GoalsBoard";

// Review-only: preview any state on any card without touching the data module.
const STATE_CYCLE = ["INTAKE", "PLAN", "EXECUTE", "TEST", "CLOSE", "ARCHIVED", "UNSORTED"];

const Board = ({ overrides = {}, variant, countOverride, ...rest }) => {
  const cards = useMemo(
    () => CARDS.slice(0, countOverride ?? CARDS.length).map((c) => ({ ...c, ...(overrides[c.slug] || {}) })),
    [overrides, countOverride],
  );
  const clock = useMemo(() => boardClock(), []);
  return <GoalsBoard cards={cards} clock={clock} variant={variant} {...rest} />;
};

const Row = ({ children }) => <div className="flex flex-wrap items-start gap-10">{children}</div>;

const LiveTab = () => (
  <Row>
    <DesktopFrame caption="Live — as committed">
      <Board />
    </DesktopFrame>
  </Row>
);

const ExpandedTab = () => (
  <Row>
    <PhoneFrame caption="Mobile · bottom sheet">
      <Board variant="mobile" initialOpenSlug={CARDS[0]?.slug} />
    </PhoneFrame>
    <DesktopFrame>
      <Board variant="desktop" initialOpenSlug={CARDS[0]?.slug} />
    </DesktopFrame>
  </Row>
);

const AllStatesTab = () => {
  const [i, setI] = useState(0);
  const bump = useCallback(() => setI((v) => (v + 1) % STATE_CYCLE.length), []);
  const overrides = useMemo(
    () => Object.fromEntries(CARDS.map((c) => [c.slug, { rawState: STATE_CYCLE[i] }])),
    [i],
  );
  return (
    <>
      <button
        type="button"
        data-testid="goals-all-states-cycle"
        onClick={bump}
        className="mb-6 inline-flex h-9 items-center rounded-control border border-os-border bg-white px-4 font-mono text-[12px] text-os-ink hover:border-os-border-strong"
      >
        Cycle every card → {STATE_CYCLE[i]}
      </button>
      <Row>
        <DesktopFrame autoHeight>
          <Board overrides={overrides} />
        </DesktopFrame>
        <PhoneFrame caption="Mobile · follows the cycle">
          <Board overrides={overrides} variant="mobile" />
        </PhoneFrame>
      </Row>
    </>
  );
};

const EmptyTab = () => (
  <Row>
    <DesktopFrame caption="Zero cards — the board explains itself">
      <Board countOverride={0} />
    </DesktopFrame>
  </Row>
);

export const GOALS_TABS = [
  { key: "live", label: "Live data", note: "The board exactly as committed — cards come straight from the chunk records ingest parsed.", Panel: LiveTab },
  { key: "expanded", label: "Expanded card", note: "A card opened: bottom sheet on mobile, side panel on desktop. Shows goal, why, skills, planned steps.", Panel: ExpandedTab },
  { key: "all-states", label: "All states", note: "Cycle every card through the seven state words — chips are words only, tokens unchanged.", Panel: AllStatesTab },
  { key: "empty-day", label: "Empty board", note: "Zero chunks. The board explains itself instead of rendering blank.", Panel: EmptyTab },
  { key: "design-dna", label: "Design DNA", note: "DNA v6 “white blueprint” — the shared sheet every screen builds on.", Panel: DnaSheet },
];